import { Box, Icon, IconButton, IconName, Logo, Nav, PancakeStack, Tag, VStack } from '@betterment/samba-react';
import { cva } from '@betterment/samba-styled-system/css';

import { SambaAutoLink } from '~/b4b/components/SambaAutoLink';
import { urlBuilder } from '~/b4b/utils/urlBuilder';

import { CurrentEmployer, Employer, LoadingStatus, PersonalProfile } from './data';
import PlanSelect from './PlanSelect';
import ProfileMenu from './ProfileMenu';

interface SidebarLayoutProps {
  reload: () => void;
  loadingStatus: LoadingStatus;
  tasksLoadingStatus: LoadingStatus;
  currentEmployer?: CurrentEmployer | null;
  personalProfile?: PersonalProfile | null;
  employers: Employer[];
  taskCounts: Record<string, number>;
  loadEmployersWithTasks: () => void;
  isMobileMenuOpen: boolean;
  toggleMobileMenu: () => void;
}

const MOBILE_HEADER_HEIGHT = '64px';
const MOBILE_SIDEBAR_WIDTH = '300px';
const DESKTOP_SIDEBAR_WIDTH = '240px';

const overlayStyles = cva({
  base: {
    display: { base: 'block', lg: 'none' },
    position: 'fixed',
    top: MOBILE_HEADER_HEIGHT,
    left: 0,
    width: '100vw',
    height: `calc(100dvh - ${MOBILE_HEADER_HEIGHT})`,
    backgroundColor: 'background.overlay',
    zIndex: 5, // style-closet z-index for overlay elements
  },
  variants: {
    isOpen: {
      true: {
        opacity: 1,
        visibility: 'visible',
        pointerEvents: 'auto',
        transition: 'opacity 250ms cubic-bezier(0, 0, 0.5, 1)',
      },
      false: {
        opacity: 0,
        visibility: 'hidden',
        pointerEvents: 'none',
        // Delay visibility until after opacity animation completes
        transition: 'opacity 250ms cubic-bezier(0, 0, 0.5, 1), visibility 0s 250ms',
      },
    },
  },
  defaultVariants: {
    isOpen: false,
  },
});

const sidebarStyles = cva({
  base: {
    backgroundColor: 'primitives.navy.100',
    px: 'space.100',
    overflowY: 'auto',
    zIndex: 5, // style-closet z-index for overlay elements
    '& a': {
      minHeight: '40px',
      fontSize: 'fontSize.50',
      gap: 'space.150',
      color: 'text.onPrimary',
      display: 'flex',
      justifyContent: 'flex-start',
      fontWeight: '500',
      px: 'space.150',
      py: 0,
      _hover: {
        backgroundColor: 'primitives.white.100alpha10',
      },
      _focus: {
        backgroundColor: 'primitives.white.100alpha10',
      },
      '&.active': {
        backgroundColor: 'primitives.white.100alpha30',
      },
    },
    // Mobile: positioned below header, slides in from left
    position: 'fixed',
    top: MOBILE_HEADER_HEIGHT,
    height: `calc(100dvh - ${MOBILE_HEADER_HEIGHT})`,
    py: 'space.100',
    width: MOBILE_SIDEBAR_WIDTH,
    // Desktop: always visible, static width, full height
    lg: {
      width: DESKTOP_SIDEBAR_WIDTH,
      position: 'static',
      height: '100dvh',
      top: 0,
      py: 'space.200',
      transform: 'translate3d(0, 0, 0)',
      visibility: 'visible',
    },
  },
  variants: {
    isOpen: {
      true: {
        transform: 'translate3d(0, 0, 0)',
        visibility: 'visible',
        transition: 'transform 250ms cubic-bezier(0, 0, 0.5, 1)',
      },
      false: {
        transform: 'translate3d(-100%, 0, 0)',
        visibility: 'hidden',
        // Delay visibility until after transform animation completes
        transition: 'transform 250ms cubic-bezier(0, 0, 0.5, 1), visibility 0s 250ms',
      },
    },
  },
  defaultVariants: {
    isOpen: false,
  },
});

const SidebarLink = ({
  href,
  children,
  active = false,
}: React.PropsWithChildren<{ href: string; active?: boolean }>) => (
  <SambaAutoLink variant="ghostPrimary" to={href} className={active ? 'active' : ''}>
    {children}
  </SambaAutoLink>
);

const SidebarIcon = ({ name }: { name: IconName }) => <Icon size="small" name={name} />;

const SidebarLayout = ({
  reload,
  loadingStatus,
  tasksLoadingStatus,
  currentEmployer,
  personalProfile,
  employers,
  taskCounts,
  loadEmployersWithTasks,
  isMobileMenuOpen,
  toggleMobileMenu,
}: SidebarLayoutProps) => {
  return (
    <>
      {/* Fixed collapsed header - mobile only */}
      <Box
        css={{
          display: { base: 'flex', lg: 'none' },
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: MOBILE_HEADER_HEIGHT,
          backgroundColor: 'primitives.navy.100',
          alignItems: 'center',
          px: 'space.200',
          zIndex: 5, // zIndexs.loader (style-closet token for overlay elements)
          gap: 'space.200',
        }}
      >
        <IconButton
          label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          name={isMobileMenuOpen ? 'X' : 'Hamburger'}
          variant="ghostPrimary"
          onClick={toggleMobileMenu}
          aria-expanded={isMobileMenuOpen}
          aria-controls="sidebar"
          css={{
            color: 'text.onPrimary',
            _hover: {
              backgroundColor: 'primitives.white.100alpha10',
            },
          }}
        />
        <SambaAutoLink to={urlBuilder.dashboard()} aria-label="Back to dashboard">
          <Logo
            data-testid="logo"
            css={{ display: 'flex', maxWidth: '192px', maxHeight: '16px' }}
            type="b4bSignatureWhite"
          />
        </SambaAutoLink>
      </Box>

      {/* Overlay backdrop - mobile only, fades in when open */}
      <Box className={overlayStyles({ isOpen: isMobileMenuOpen })} onClick={toggleMobileMenu} />

      {/* Sidebar */}
      <Box id="sidebar" className={sidebarStyles({ isOpen: isMobileMenuOpen })}>
        <PancakeStack
          css={{
            animation: 'fadeIn 0.10s ease-in',
            height: '100%',
            minHeight: 'auto',
            gap: 'space.300',
            minWidth: '220px',
          }}
        >
          <PancakeStack.Header>
            <VStack css={{ gap: 'space.300' }}>
              {/* Logo - desktop only (mobile logo is in fixed header) */}
              <SambaAutoLink to={urlBuilder.dashboard()} aria-label="Back to dashboard">
                <Box css={{ display: { base: 'none', lg: 'block' } }}>
                  <Logo css={{ p: 'space.150', maxHeight: '16px' }} type="b4bSignatureWhite" />
                </Box>
              </SambaAutoLink>
              {employers.length > 1 && (
                <PlanSelect
                  reload={reload}
                  loadingStatus={loadingStatus}
                  tasksLoadingStatus={tasksLoadingStatus}
                  currentEmployer={currentEmployer}
                  employers={employers}
                  taskCounts={taskCounts}
                  loadEmployersWithTasks={loadEmployersWithTasks}
                />
              )}
            </VStack>
          </PancakeStack.Header>
          <PancakeStack.Main>
            <Nav aria-label="Overview">
              <Nav.List>
                <Nav.ListItem>
                  <SidebarLink href={urlBuilder.dashboard()}>
                    <SidebarIcon name="Home" />
                    Home
                  </SidebarLink>
                  {currentEmployer?.onboarding &&
                    (currentEmployer.onboarding.status === 'CREATED' ||
                      currentEmployer.onboarding.status === 'ON_HOLD') && (
                      <SidebarLink href={urlBuilder.onboarding()}>
                        <SidebarIcon name="Map" />
                        Onboarding
                      </SidebarLink>
                    )}
                  <SidebarLink href={urlBuilder.tasks()}>
                    <SidebarIcon name="InfoBox" />
                    <Box css={{ flexGrow: 1, textAlign: 'left' }}>Tasks</Box>
                    {currentEmployer && currentEmployer?.tasks.count > 0 && (
                      <Tag accent="blue">{currentEmployer.tasks.count}</Tag>
                    )}
                  </SidebarLink>
                  <SidebarLink href={urlBuilder.plan()}>
                    <SidebarIcon name="Briefcase" />
                    401(k)
                  </SidebarLink>
                  {currentEmployer?.fiveTwoNineBenefit?.status === 'ACTIVE' && (
                    <SidebarLink href={urlBuilder.fiveTwoNine()}>
                      <SidebarIcon name="Education" />
                      529
                    </SidebarLink>
                  )}
                </Nav.ListItem>
              </Nav.List>
            </Nav>
            <Nav.Separator />
            <Nav aria-label="Tools">
              <Nav.List>
                <Nav.ListItem>
                  <SidebarLink href={urlBuilder.employees()}>
                    <SidebarIcon name="Person" />
                    Employees
                  </SidebarLink>
                  <SidebarLink href={urlBuilder.payroll()}>
                    <SidebarIcon name="Cash" />
                    Payroll
                  </SidebarLink>
                  <SidebarLink href={urlBuilder.compliance()}>
                    <SidebarIcon name="SecurityCheck" />
                    Compliance
                  </SidebarLink>
                  <SidebarLink href={urlBuilder.reports({})}>
                    <SidebarIcon name="Bar" />
                    Reports
                  </SidebarLink>
                </Nav.ListItem>
              </Nav.List>
            </Nav>
            <Nav.Separator />
            <Nav aria-label="Account and resources">
              <Nav.List>
                <Nav.ListItem>
                  <SidebarLink href={urlBuilder.help()}>
                    <SidebarIcon name="QuestionMarkCircle" />
                    Help
                  </SidebarLink>
                  <SidebarLink href={urlBuilder.resources()}>
                    <SidebarIcon name="Lightbulb" />
                    Resources
                  </SidebarLink>
                  <SidebarLink href={urlBuilder.settings()}>
                    <SidebarIcon name="Gear" />
                    Settings
                  </SidebarLink>
                </Nav.ListItem>
              </Nav.List>
            </Nav>
          </PancakeStack.Main>
          <PancakeStack.Footer>
            <ProfileMenu loadingStatus={loadingStatus} personalProfile={personalProfile} reload={reload} />
          </PancakeStack.Footer>
        </PancakeStack>
      </Box>
    </>
  );
};

export default SidebarLayout;
